/**
 * Upload wait ceilings, kept in their own module so they can be unit-tested in
 * the Node test environment. `upload-from-uri.ts` imports `react-native` for
 * `Platform`, which vitest cannot parse (Flow syntax) — same reason
 * `maps-loader.ts` keeps its logic free of react-native imports.
 */

/**
 * Hard ceiling on how long we WAIT for a single transfer.
 *
 * A PUT whose response never comes back (WiFi -> cellular handoff, iOS suspend,
 * half-open socket) used to leave the caller awaiting forever: the bytes land in
 * S3, the promise never settles, and every step after it is skipped in silence.
 * Production incident 2026-08-11 — 80 medication photos across 9 visits reached
 * S3 and were never extracted, because the batch loop was still awaiting a PUT
 * that had already completed on the wire. Nothing threw, so no error surfaced to
 * the RN and nothing reached Sentry.
 */
export const UPLOAD_TIMEOUT_MS = 90_000;

/**
 * Ceiling for a whole staged batch. Generous enough for a large medication
 * batch on a weak signal (the biggest real batches run ~40 photos), tight
 * enough that a stalled run surfaces an error inside one visit rather than
 * never.
 */
export const BATCH_UPLOAD_DEADLINE_MS = 10 * 60_000;

/** Thrown when a transfer, or a whole batch, exceeds its ceiling. */
export class UploadTimeoutError extends Error {
    constructor(ms: number) {
        super(`Upload timed out after ${Math.round(ms / 1000)}s`);
        this.name = "UploadTimeoutError";
    }
}

/**
 * Reject with `UploadTimeoutError` if `promise` has not settled within `ms`.
 *
 * This bounds the WAIT, not the work: `FileSystem.uploadAsync` exposes no abort
 * handle, so the native transfer may still complete in the background. That is
 * fine and intentional — the point is that the caller regains control, can show
 * an error and let the RN retry, instead of hanging forever.
 */
export function withUploadTimeout<T>(
    promise: Promise<T>,
    ms = UPLOAD_TIMEOUT_MS,
    /**
     * Called when the ceiling is hit, before rejecting. Native uploads started
     * via `createUploadTask` pass `cancelAsync` here so the stalled transfer is
     * actually torn down rather than merely abandoned.
     */
    onTimeout?: () => void
): Promise<T> {
    // Once we stop waiting, the underlying transfer can still reject minutes
    // later. Attach a no-op handler so that late rejection is not reported as an
    // unhandled promise rejection (which in RN surfaces as a red-box warning).
    promise.catch(() => {});
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
            try {
                onTimeout?.();
            } catch {
                // Cancelling is best-effort; never mask the timeout itself.
            }
            reject(new UploadTimeoutError(ms));
        }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => {
        if (timer !== undefined) {
            clearTimeout(timer);
        }
    });
}
